import * as Sentry from '@sentry/react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { SourceSummariesUnavailableError, fetchSourceSummaries } from '@/api/content/refs'
import type { SourceSummary } from '@/api/content/refs'

const SOURCE_DETAILS_ERROR = 'Source details could not be loaded'
const SOURCE_DETAILS_UNAVAILABLE = 'Some source details are no longer available'
const MAX_SOURCE_SUMMARY_IDS = 100

interface SourceDetailsState {
  batchKey: string
  errorMessage: string | null
  loading: boolean
  retryable: boolean
  summaries: Array<SourceSummary> | null
}

export interface SourceDetailsController {
  errorMessage: string | null
  load: () => void
  loading: boolean
  retry: () => void
  retryable: boolean
  summaries: Array<SourceSummary> | null
}

export type SourceSummaryLoader = (docIds: Array<number>) => Promise<Array<SourceSummary>>

function documentIDBatchKey(docIds: Array<number>): string | null {
  const unique = new Set<number>()
  for (const docId of docIds) {
    if (!Number.isSafeInteger(docId) || docId <= 0) {
      return null
    }
    unique.add(docId)
  }
  if (unique.size > MAX_SOURCE_SUMMARY_IDS) {
    return null
  }
  return [...unique].join(',')
}

function orderCompleteBatch(docIds: Array<number>, summaries: Array<SourceSummary>): Array<SourceSummary> {
  if (summaries.length !== docIds.length) {
    throw new Error('Source summary response did not contain the complete requested batch')
  }

  const requested = new Set(docIds)
  const byID = new Map<number, SourceSummary>()
  for (const summary of summaries) {
    if (!requested.has(summary.docId) || byID.has(summary.docId)) {
      throw new Error('Source summary response did not match the requested batch')
    }
    byID.set(summary.docId, summary)
  }

  return docIds.map((docId) => {
    const summary = byID.get(docId)
    if (!summary) {
      throw new Error('Source summary response did not contain the complete requested batch')
    }
    return summary
  })
}

export function useSourceDetails(
  docIds: Array<number>,
  loadSummaries: SourceSummaryLoader = fetchSourceSummaries
): SourceDetailsController {
  const inputKey = JSON.stringify(docIds)
  const normalizedBatchKey = documentIDBatchKey(docIds)
  const batchKey = normalizedBatchKey ?? `invalid:${inputKey}`
  const normalizedDocIDs = useMemo(
    () => (normalizedBatchKey ? normalizedBatchKey.split(',').map(Number) : []),
    [normalizedBatchKey]
  )
  const [state, setState] = useState<SourceDetailsState>({
    batchKey,
    errorMessage: null,
    loading: false,
    retryable: false,
    summaries: null
  })
  const requestVersionRef = useRef(0)
  const loadingRequestRef = useRef<{ batchKey: string; requestVersion: number } | null>(null)
  const successfulBatchesRef = useRef(new Map<string, Array<SourceSummary>>())

  const load = useCallback(() => {
    const cached = successfulBatchesRef.current.get(batchKey)
    if (cached) {
      requestVersionRef.current += 1
      loadingRequestRef.current = null
      setState({ batchKey, errorMessage: null, loading: false, retryable: false, summaries: cached })
      return
    }
    if (loadingRequestRef.current?.batchKey === batchKey) {
      return
    }
    if (normalizedBatchKey === null) {
      requestVersionRef.current += 1
      loadingRequestRef.current = null
      setState({ batchKey, errorMessage: SOURCE_DETAILS_ERROR, loading: false, retryable: false, summaries: null })
      return
    }
    if (normalizedDocIDs.length === 0) {
      requestVersionRef.current += 1
      loadingRequestRef.current = null
      const empty: Array<SourceSummary> = []
      successfulBatchesRef.current.set(batchKey, empty)
      setState({ batchKey, errorMessage: null, loading: false, retryable: false, summaries: empty })
      return
    }

    const requestVersion = requestVersionRef.current + 1
    requestVersionRef.current = requestVersion
    loadingRequestRef.current = { batchKey, requestVersion }
    setState({ batchKey, errorMessage: null, loading: true, retryable: false, summaries: null })
    void loadSummaries(normalizedDocIDs)
      .then((response) => orderCompleteBatch(normalizedDocIDs, response))
      .then((summaries) => {
        if (requestVersionRef.current !== requestVersion) return
        successfulBatchesRef.current.set(batchKey, summaries)
        setState({ batchKey, errorMessage: null, loading: false, retryable: false, summaries })
      })
      .catch((error: unknown) => {
        if (requestVersionRef.current !== requestVersion) return
        const unavailable = error instanceof SourceSummariesUnavailableError
        if (!unavailable) Sentry.captureException(error)
        setState({
          batchKey,
          errorMessage: unavailable ? SOURCE_DETAILS_UNAVAILABLE : SOURCE_DETAILS_ERROR,
          loading: false,
          retryable: !unavailable,
          summaries: null
        })
      })
      .finally(() => {
        if (
          loadingRequestRef.current?.batchKey === batchKey &&
          loadingRequestRef.current.requestVersion === requestVersion
        ) {
          loadingRequestRef.current = null
        }
      })
  }, [batchKey, loadSummaries, normalizedBatchKey, normalizedDocIDs])

  const currentState = state.batchKey === batchKey ? state : null
  return {
    errorMessage: currentState?.errorMessage ?? null,
    load,
    loading: currentState?.loading ?? false,
    retry: load,
    retryable: currentState?.retryable ?? false,
    summaries: currentState?.summaries ?? null
  }
}

export function sourceOriginLabel(summary: SourceSummary): string {
  switch (summary.connectorType) {
    case 'google-drive':
      return 'Google Drive'
    case 'sharepoint':
      return 'SharePoint'
    case 'web-crawler':
      return 'Web crawler'
    default:
      return summary.docType === 'connector_file' || summary.docType === 'connector_webcrawler'
        ? 'Connected content'
        : 'Workspace content'
  }
}

function formatSourceTime(timestamp: number): string {
  return new Date(timestamp).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

export interface SourceSummaryRowProps {
  capturedFileName?: string | undefined
  summary: SourceSummary
}

export function SourceSummaryRow({ capturedFileName, summary }: SourceSummaryRowProps) {
  const currentFileName = summary.fileName.trim() || 'Workspace source'
  const capturedName = capturedFileName?.trim() || undefined
  const primaryName = capturedName || currentFileName
  const renamed = capturedName !== undefined && capturedName !== currentFileName
  const connectorName = summary.connectorName?.trim()
  const locator = summary.locator?.trim()
  const isConnector = summary.docType === 'connector_file' || summary.docType === 'connector_webcrawler'
  const freshness =
    summary.lastSyncedAtTsMs !== undefined
      ? `synced ${formatSourceTime(summary.lastSyncedAtTsMs)}`
      : isConnector
        ? undefined
        : `updated ${formatSourceTime(summary.updatedAtTsMs)}`
  const metadata = [sourceOriginLabel(summary), connectorName, freshness].filter(Boolean).join(' · ')

  return (
    <div role="listitem" className="min-w-0 py-1.5 text-sm">
      <p className="leading-snug font-medium break-words text-foreground">{primaryName}</p>
      {renamed && <p className="text-xs break-words text-muted-foreground">Now named {currentFileName}</p>}
      {locator && <p className="text-xs break-words text-muted-foreground">{locator}</p>}
      <p className="text-xs break-words text-muted-foreground">{metadata}</p>
    </div>
  )
}
